import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { auth } from '../../config/firebase';
import courseService, { Course, IndustryCategory, CourseTemplate } from '../../services/courseService';
import toast from 'react-hot-toast';
import { comprehensiveReactCourse } from '../../utils/comprehensiveReactCourse';
import { workingFinanceCourse } from '../../utils/workingFinanceCourse';
import { simpleFinanceCourse } from '../../utils/simpleFinanceCourse';
import { comprehensiveFinanceCourse } from '../../utils/comprehensiveFinanceCourse';
import { investmentAnalysisCourse } from '../../utils/investmentAnalysisCourse';
import { financialMarketsCourse } from '../../utils/financialMarketsCourse';
import { businessEconomicsCourse } from '../../utils/businessEconomicsCourse';
import { quantitativeMethodsCourse } from '../../utils/quantitativeMethodsCourse';
import { financialStatementAnalysisCourse } from '../../utils/financialStatementAnalysisCourse';
import { riskManagementCourse } from '../../utils/riskManagementCourse';
import { internationalFinanceCourse } from '../../utils/internationalFinanceCourse';
import { corporateGovernanceCourse } from '../../utils/corporateGovernanceCourse';
import { personalFinanceCourse } from '../../utils/personalFinanceCourse';
import { behavioralFinanceCourse } from '../../utils/behavioralFinanceCourse';
import { ethicsInFinanceCourse } from '../../utils/ethicsInFinanceCourse';
import { communicationTheoryCourse } from '../../utils/communicationTheoryCourse';
import { englishCompositionCourse } from '../../utils/englishCompositionCourse';
import { mediaStudiesCourse } from '../../utils/mediaStudiesCourse';
import { publicSpeakingCourse } from '../../utils/publicSpeakingCourse';
import { collegeMathematicsCourse } from '../../utils/collegeMathematicsCourse';
import { interpersonalCommunicationCourse } from '../../utils/interpersonalCommunicationCourse';
import { digitalMediaLiteracyCourse } from '../../utils/digitalMediaLiteracyCourse';
import { journalismCourse } from '../../utils/journalismCourse';
import { researchMethodsCourse } from '../../utils/researchMethodsCourse';
import { psychologyOfCommunicationCourse } from '../../utils/psychologyOfCommunicationCourse';
import { introToProgrammingCourse } from '../../utils/introToProgrammingCourse';
import { introToComputerScienceCourse } from '../../utils/introToComputerScienceCourse';
import { logicDiscreteStructuresCourse } from '../../utils/logicDiscreteStructuresCourse';
import { calculusICourse } from '../../utils/calculusICourse';
import { objectOrientedProgrammingCourse } from '../../utils/objectOrientedProgrammingCourse';
import { calculusIICourse } from '../../utils/calculusIICourse';
import { computerSystemsArchitectureCourse } from '../../utils/computerSystemsArchitectureCourse';
import { technicalWritingCourse } from '../../utils/technicalWritingCourse';
import { physicsICourse } from '../../utils/physicsICourse';
import { dataStructuresAlgorithmsCourse } from '../../utils/dataStructuresAlgorithmsCourse';
import { linearAlgebraCourse } from '../../utils/linearAlgebraCourse';
import { databaseSystemsCourse } from '../../utils/databaseSystemsCourse';
import { webDevelopmentCourse } from '../../utils/webDevelopmentCourse';
import { statisticsCourse } from '../../utils/statisticsCourse';
import { moneyAndBankingCourse } from '../../utils/moneyAndBankingCourse';
import { internationalBusinessCourse } from '../../utils/internationalBusinessCourse';
import { marketingPrinciplesCourse } from '../../utils/marketingPrinciplesCourse';
import { operationsManagementCourse } from '../../utils/operationsManagementCourse';
import { businessEthicsCourse } from '../../utils/businessEthicsCourse';
import { realEstateFinanceCourse } from '../../utils/realEstateFinanceCourse';
import { integratedMarketingCommunicationCourse } from '../../utils/integratedMarketingCommunicationCourse';
import { podcastAudioProductionCourse } from '../../utils/podcastAudioProductionCourse';
import { globalCommunicationCourse } from '../../utils/globalCommunicationCourse';
import { socialScienceCourses } from '../../utils/socialScienceCourses';
import api from '../../services/apiService';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  StarIcon,
  UserGroupIcon,
  EyeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  XMarkIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  VideoCameraIcon,
  CloudArrowUpIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const CourseManagement: React.FC = () => {
  const { adminUser } = useAdmin();

  // Helper function to get admin token
  const getAdminToken = async (): Promise<string | null> => {
    try {
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        console.log('Got admin token, length:', token?.length || 0);
        return token;
      }
      console.warn('No current admin user found');
      return null;
    } catch (error) {
      console.error('Error getting admin token:', error);
      return null;
    }
  };
  const [courses, setCourses] = useState<Course[]>([]);
  const [industryCategories, setIndustryCategories] = useState<IndustryCategory[]>([]);
  const [courseTemplates, setCourseTemplates] = useState<CourseTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [editThumbnail, setEditThumbnail] = useState<File | null>(null);
  const [editThumbnailPreview, setEditThumbnailPreview] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);
  const [creatingCourses, setCreatingCourses] = useState<{[key: string]: boolean}>({});
  const [courseExists, setCourseExists] = useState<{[key: string]: boolean}>({});
  const [hideDeployed, setHideDeployed] = useState(false);
  const [updatingStatuses, setUpdatingStatuses] = useState(false);

  // Define all predefined courses
  const predefinedCourses = [
    { 
      key: 'react-mastery', 
      data: comprehensiveReactCourse, 
      name: 'React Mastery Course', 
      icon: '🚀',
      description: 'Comprehensive 8+ hour React course with advanced topics',
      color: 'purple',
      features: ['5+ Complete Modules', 'Interactive Exercises', 'Video Content', 'Hands-on Projects', 'Comprehensive Quizzes', 'AI-Graded Assignments']
    },
    { 
      key: 'working-finance', 
      data: workingFinanceCourse, 
      name: 'Working Finance Course', 
      icon: '📊',
      description: 'Practical finance course with real-world applications',
      color: 'green',
      features: ['Real-Time Stock Analysis', 'Balance Sheet Modeling', 'Portfolio Optimization', 'Risk Assessment Tools']
    },
    { 
      key: 'simple-finance', 
      data: simpleFinanceCourse, 
      name: 'Simple Finance Course', 
      icon: '💰',
      description: 'Beginner-friendly introduction to finance fundamentals',
      color: 'blue',
      features: ['Basic Financial Concepts', 'Personal Budgeting', 'Simple Investment Strategies', 'Financial Planning']
    },
    { 
      key: 'comprehensive-finance', 
      data: comprehensiveFinanceCourse, 
      name: 'Comprehensive Finance Course', 
      icon: '📈',
      description: 'Advanced finance course covering all major topics',
      color: 'indigo',
      features: ['Advanced Financial Analysis', 'Corporate Finance', 'Investment Strategies', 'Financial Modeling']
    },
    { 
      key: 'investment-analysis', 
      data: investmentAnalysisCourse, 
      name: 'Investment Analysis Course', 
      icon: '🔍',
      description: 'Deep dive into investment analysis and valuation',
      color: 'emerald',
      features: ['Security Analysis', 'Valuation Methods', 'Portfolio Theory', 'Market Analysis']
    },
    { 
      key: 'financial-markets', 
      data: financialMarketsCourse, 
      name: 'Financial Markets Course', 
      icon: '🏦',
      description: 'Understanding financial markets and instruments',
      color: 'cyan',
      features: ['Market Structure', 'Trading Mechanisms', 'Financial Instruments', 'Market Efficiency']
    },
    { 
      key: 'business-economics', 
      data: businessEconomicsCourse, 
      name: 'Business Economics Course', 
      icon: '🏢',
      description: 'Economics principles applied to business decisions',
      color: 'amber',
      features: ['Microeconomic Theory', 'Market Analysis', 'Business Strategy', 'Economic Forecasting']
    },
    { 
      key: 'quantitative-methods', 
      data: quantitativeMethodsCourse, 
      name: 'Quantitative Methods Course', 
      icon: '📐',
      description: 'Statistical and mathematical methods for finance',
      color: 'rose',
      features: ['Statistical Analysis', 'Financial Modeling', 'Risk Measurement', 'Data Analysis']
    },
    { 
      key: 'financial-statement-analysis', 
      data: financialStatementAnalysisCourse, 
      name: 'Financial Statement Analysis', 
      icon: '📋',
      description: 'Analyzing and interpreting financial statements',
      color: 'violet',
      features: ['Balance Sheet Analysis', 'Income Statement Review', 'Cash Flow Analysis', 'Ratio Analysis']
    },
    { 
      key: 'risk-management', 
      data: riskManagementCourse, 
      name: 'Risk Management Course', 
      icon: '🛡️',
      description: 'Identifying and managing financial risks',
      color: 'orange',
      features: ['Risk Assessment', 'Hedging Strategies', 'Derivative Instruments', 'Risk Metrics']
    },
    { 
      key: 'international-finance', 
      data: internationalFinanceCourse, 
      name: 'International Finance Course', 
      icon: '🌍',
      description: 'Finance in global markets and currencies',
      color: 'teal',
      features: ['Foreign Exchange', 'International Trade', 'Global Markets', 'Currency Risk']
    },
    { 
      key: 'corporate-governance', 
      data: corporateGovernanceCourse, 
      name: 'Corporate Governance Course', 
      icon: '⚖️',
      description: 'Corporate governance principles and practices',
      color: 'slate',
      features: ['Board Oversight', 'Executive Compensation', 'Shareholder Rights', 'Compliance']
    },
    { 
      key: 'personal-finance', 
      data: personalFinanceCourse, 
      name: 'Personal Finance Course', 
      icon: '💳',
      description: 'Managing personal finances and investments',
      color: 'lime',
      features: ['Personal Budgeting', 'Investment Planning', 'Retirement Savings', 'Tax Strategies']
    },
    { 
      key: 'behavioral-finance', 
      data: behavioralFinanceCourse, 
      name: 'Behavioral Finance Course', 
      icon: '🧠',
      description: 'Psychology and behavior in financial decision making',
      color: 'pink',
      features: ['Cognitive Biases', 'Market Psychology', 'Behavioral Models', 'Decision Making']
    },
    { 
      key: 'ethics-in-finance', 
      data: ethicsInFinanceCourse, 
      name: 'Ethics in Finance Course', 
      icon: '🤝',
      description: 'Ethical considerations in financial practice',
      color: 'gray',
      features: ['Professional Ethics', 'Fiduciary Duty', 'Conflicts of Interest', 'Compliance Standards']
    },
    // Communications Courses
    { 
      key: 'communication-theory', 
      data: communicationTheoryCourse, 
      name: 'Introduction to Communication Theory', 
      icon: '📡',
      description: 'Foundational theories and models of human communication',
      color: 'cyan',
      features: ['Communication Models', 'Interpersonal Theory', 'Media Theory', 'Cultural Communication']
    },
    { 
      key: 'english-composition', 
      data: englishCompositionCourse, 
      name: 'English Composition', 
      icon: '✍️',
      description: 'Academic writing and composition skills development',
      color: 'rose',
      features: ['Essay Writing', 'Grammar & Style', 'Research Writing', 'Critical Analysis']
    },
    { 
      key: 'media-studies', 
      data: mediaStudiesCourse, 
      name: 'Introduction to Media Studies', 
      icon: '📺',
      description: 'Critical analysis of media forms and their cultural impact',
      color: 'orange',
      features: ['Media Analysis', 'Digital Culture', 'Media History', 'Critical Theory']
    },
    { 
      key: 'public-speaking', 
      data: publicSpeakingCourse, 
      name: 'Public Speaking Fundamentals', 
      icon: '🎤',
      description: 'Develop confidence and skills in public speaking',
      color: 'amber',
      features: ['Speech Delivery', 'Audience Analysis', 'Presentation Skills', 'Confidence Building']
    },
    { 
      key: 'college-mathematics', 
      data: collegeMathematicsCourse, 
      name: 'College Mathematics', 
      icon: '🔢',
      description: 'Essential mathematical concepts for communication research',
      color: 'lime',
      features: ['Statistics', 'Data Analysis', 'Research Methods', 'Mathematical Reasoning']
    },
    { 
      key: 'interpersonal-communication', 
      data: interpersonalCommunicationCourse, 
      name: 'Interpersonal Communication', 
      icon: '👥',
      description: 'Understanding and improving one-on-one communication',
      color: 'teal',
      features: ['Relationship Communication', 'Conflict Resolution', 'Active Listening', 'Emotional Intelligence']
    },
    { 
      key: 'digital-media-literacy', 
      data: digitalMediaLiteracyCourse, 
      name: 'Digital Media Literacy', 
      icon: '💻',
      description: 'Navigate and critically evaluate digital media content',
      color: 'sky',
      features: ['Digital Literacy', 'Information Evaluation', 'Online Safety', 'Digital Citizenship']
    },
    { 
      key: 'journalism', 
      data: journalismCourse, 
      name: 'Introduction to Journalism', 
      icon: '📰',
      description: 'Fundamentals of news gathering, writing, and reporting',
      color: 'violet',
      features: ['News Writing', 'Investigative Reporting', 'Media Ethics', 'Digital Journalism']
    },
    { 
      key: 'research-methods', 
      data: researchMethodsCourse, 
      name: 'Research Methods', 
      icon: '🔬',
      description: 'Quantitative and qualitative research methodologies',
      color: 'fuchsia',
      features: ['Survey Research', 'Interview Techniques', 'Data Collection', 'Statistical Analysis']
    },
    { 
      key: 'psychology-communication', 
      data: psychologyOfCommunicationCourse, 
      name: 'Psychology of Communication', 
      icon: '🧠',
      description: 'Psychological principles underlying human communication',
      color: 'pink',
      features: ['Cognitive Processes', 'Social Psychology', 'Persuasion Theory', 'Communication Barriers']
    },
    { 
      key: 'intro-programming-101', 
      data: introToProgrammingCourse, 
      name: 'Introduction to Programming', 
      icon: '💻',
      description: 'Comprehensive introduction to programming fundamentals using JavaScript',
      color: 'orange',
      features: ['Programming Basics', 'JavaScript Fundamentals', 'Problem Solving', 'Hands-on Coding', 'Interactive Exercises']
    },
    { 
      key: 'intro-computer-science', 
      data: introToComputerScienceCourse, 
      name: 'Introduction to Computer Science', 
      icon: '🖥️',
      description: 'Fundamental concepts of computer science including algorithms and data structures',
      color: 'cyan',
      features: ['Algorithms', 'Data Structures', 'Computational Thinking', 'Programming Concepts', 'Problem Analysis']
    },
    { 
      key: 'logic-discrete-structures', 
      data: logicDiscreteStructuresCourse, 
      name: 'Logic and Discrete Structures', 
      icon: '🔢',
      description: 'Mathematical logic and discrete structures for computer science',
      color: 'teal',
      features: ['Mathematical Logic', 'Set Theory', 'Functions & Relations', 'Graph Theory', 'Proof Techniques']
    },
    { 
      key: 'calculus-1-101', 
      data: calculusICourse, 
      name: 'Calculus I', 
      icon: '∫',
      description: 'Differential calculus with applications to computer science',
      color: 'lime',
      features: ['Limits & Continuity', 'Derivatives', 'Applications', 'Optimization', 'Mathematical Modeling']
    },
    { 
      key: 'object-oriented-programming', 
      data: objectOrientedProgrammingCourse, 
      name: 'Object-Oriented Programming', 
      icon: '🏗️',
      description: 'Advanced programming with OOP principles, design patterns, and software architecture',
      color: 'red',
      features: ['OOP Fundamentals', 'Design Patterns', 'SOLID Principles', 'Testing & Debugging', 'Software Architecture']
    },
    { 
      key: 'calculus-ii', 
      data: calculusIICourse, 
      name: 'Calculus II', 
      icon: '∑',
      description: 'Advanced calculus covering integration, series, and multivariable foundations',
      color: 'yellow',
      features: ['Integration Techniques', 'Infinite Series', 'Parametric Equations', 'Multivariable Concepts', 'Applications']
    },
    { 
      key: 'computer-systems-architecture', 
      data: computerSystemsArchitectureCourse, 
      name: 'Computer Systems Architecture', 
      icon: '🖥️',
      description: 'Computer organization, processor architecture, and system-level programming',
      color: 'zinc',
      features: ['Computer Organization', 'Assembly Programming', 'Memory Systems', 'Performance Analysis', 'System Design']
    },
    { 
      key: 'technical-writing', 
      data: technicalWritingCourse, 
      name: 'Technical Writing', 
      icon: '📝',
      description: 'Professional technical communication and documentation for technology fields',
      color: 'stone',
      features: ['Technical Documentation', 'API Documentation', 'Proposal Writing', 'Content Strategy', 'Collaborative Writing']
    },
    { 
      key: 'physics-i', 
      data: physicsICourse, 
      name: 'Physics I: Mechanics', 
      icon: '⚛️',
      description: 'Classical mechanics with computational applications and simulation techniques',
      color: 'neutral',
      features: ['Classical Mechanics', 'Mathematical Modeling', 'Computational Physics', 'Simulation Techniques', 'Real-World Applications']
    },
    { 
      key: 'data-structures-algorithms', 
      data: dataStructuresAlgorithmsCourse, 
      name: 'Data Structures and Algorithms', 
      icon: '🌲',
      description: 'Comprehensive study of data structures, algorithms, and computational complexity',
      color: 'green',
      features: ['Arrays & Linked Lists', 'Trees & Graphs', 'Sorting Algorithms', 'Dynamic Programming', 'Complexity Analysis', 'Problem Solving']
    },
    { 
      key: 'linear-algebra', 
      data: linearAlgebraCourse, 
      name: 'Linear Algebra', 
      icon: '📐',
      description: 'Mathematical foundations of vectors, matrices, linear transformations, and applications',
      color: 'blue',
      features: ['Vector Operations', 'Matrix Algebra', 'Linear Transformations', 'Eigenvalues & Eigenvectors', 'Computer Graphics', 'Applications']
    },
    { 
      key: 'database-systems', 
      data: databaseSystemsCourse, 
      name: 'Database Systems', 
      icon: '🗄️',
      description: 'Comprehensive study of database design, implementation, and management',
      color: 'indigo',
      features: ['Database Design', 'SQL Mastery', 'Normalization', 'ER Modeling', 'Query Optimization', 'Transaction Processing']
    },
    { 
      key: 'web-development', 
      data: webDevelopmentCourse, 
      name: 'Web Development', 
      icon: '🌐',
      description: 'Modern web development with HTML, CSS, JavaScript, React, and full-stack technologies',
      color: 'purple',
      features: ['Frontend Development', 'React Applications', 'Node.js Backend', 'Database Integration', 'Responsive Design', 'Modern Tools']
    },
    { 
      key: 'statistics', 
      data: statisticsCourse, 
      name: 'Statistics', 
      icon: '📊',
      description: 'Statistical methods, probability theory, data analysis, and statistical inference',
      color: 'emerald',
      features: ['Descriptive Statistics', 'Probability Theory', 'Statistical Inference', 'Data Visualization', 'Hypothesis Testing', 'Real-World Applications']
    },
    // Finance Interactive Courses
    { 
      key: 'money-and-banking', 
      data: moneyAndBankingCourse, 
      name: 'Money and Banking', 
      icon: '🏦',
      description: 'Monetary systems, banking operations, central bank policies, and financial markets',
      color: 'blue',
      features: ['Monetary Policy', 'Banking Operations', 'Financial Markets', 'Central Banking', 'Finance Interactive']
    },
    { 
      key: 'international-business', 
      data: internationalBusinessCourse, 
      name: 'International Business', 
      icon: '🌍',
      description: 'Global business operations, international trade, cross-cultural management, and multinational strategies',
      color: 'teal',
      features: ['Global Strategy', 'Cross-Cultural Management', 'International Trade', 'Risk Management', 'Finance Interactive']
    },
    { 
      key: 'marketing-principles', 
      data: marketingPrinciplesCourse, 
      name: 'Marketing Principles', 
      icon: '📈',
      description: 'Marketing fundamentals, consumer behavior, digital marketing, and marketing analytics',
      color: 'orange',
      features: ['Consumer Behavior', 'Digital Marketing', 'Marketing Mix', 'Analytics', 'Finance Interactive']
    },
    { 
      key: 'operations-management', 
      data: operationsManagementCourse, 
      name: 'Operations Management', 
      icon: '⚙️',
      description: 'Operations strategy, process management, supply chain optimization, and lean operations',
      color: 'gray',
      features: ['Process Design', 'Supply Chain', 'Quality Management', 'Lean Operations', 'Finance Interactive']
    },
    { 
      key: 'business-ethics', 
      data: businessEthicsCourse, 
      name: 'Business Ethics', 
      icon: '⚖️',
      description: 'Ethical principles in business, corporate social responsibility, and stakeholder management',
      color: 'slate',
      features: ['Ethical Decision Making', 'CSR', 'Stakeholder Management', 'Compliance', 'Finance Interactive']
    },
    { 
      key: 'real-estate-finance', 
      data: realEstateFinanceCourse, 
      name: 'Real Estate Finance', 
      icon: '🏘️',
      description: 'Real estate financing, investment analysis, property valuation, and development finance',
      color: 'amber',
      features: ['Property Valuation', 'Investment Analysis', 'Mortgage Markets', 'Development Finance', 'Finance Interactive']
    },
    // Communication Interactive Courses
    { 
      key: 'integrated-marketing-communication', 
      data: integratedMarketingCommunicationCourse, 
      name: 'Integrated Marketing Communication', 
      icon: '📢',
      description: 'IMC strategies, brand messaging, multi-channel campaigns, and communication effectiveness',
      color: 'pink',
      features: ['Brand Messaging', 'Multi-Channel Campaigns', 'Creative Strategy', 'ROI Measurement', 'Communication Interactive']
    },
    { 
      key: 'podcast-audio-production', 
      data: podcastAudioProductionCourse, 
      name: 'Podcast and Audio Production', 
      icon: '🎙️',
      description: 'Podcast creation, audio production, content development, and monetization strategies',
      color: 'purple',
      features: ['Audio Production', 'Content Development', 'Storytelling', 'Monetization', 'Communication Interactive']
    },
    { 
      key: 'global-communication', 
      data: globalCommunicationCourse, 
      name: 'Global Communication', 
      icon: '🌐',
      description: 'International communication, cross-cultural messaging, and global media systems',
      color: 'sky',
      features: ['Cross-Cultural Communication', 'Global Media', 'Cultural Adaptation', 'International PR', 'Communication Interactive']
    },
    // Social Science Courses
    ...socialScienceCourses.map((course, index) => ({
      key: course.id,
      data: course,
      name: course.title,
      icon: ['🏛️', '🧠', '📚', '🏛️', '🌍', '📊', '💹', '🤔', '👥'][index] || '🎓',
      description: course.shortDescription,
      color: ['indigo', 'purple', 'blue', 'green', 'orange', 'red', 'yellow', 'pink', 'teal'][index] || 'gray',
      features: course.skills.slice(0, 4)
    }))
  ];

  // Create course form state
  const [createForm, setCreateForm] = useState({
    title: '',
    shortDescription: '',
    description: '',
    category: '',
    industryCategory: '',
    difficulty: 'beginner' as 'beginner' | 'intermediate' | 'advanced',
    duration: 0,
    estimatedWeeks: 0,
    price: 0,
    instructorName: '',
    instructorBio: '',
    thumbnailUrl: '',
    skills: [] as string[],
    prerequisites: [] as string[],
    learningObjectives: [] as string[],
    template: '',
    tags: [] as string[]
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    shortDescription: '',
    description: '',
    category: '',
    industryCategory: '',
    difficulty: 'beginner' as 'beginner' | 'intermediate' | 'advanced',
    duration: 0,
    estimatedWeeks: 0,
    price: 0,
    instructorName: '',
    instructorBio: '',
    thumbnailUrl: '',
    skills: [] as string[],
    prerequisites: [] as string[],
    learningObjectives: [] as string[],
    template: '',
    tags: [] as string[],
    status: 'draft' as 'draft' | 'published' | 'archived'
  });

  // AI generation state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiContentType, setAiContentType] = useState<'module' | 'quiz' | 'assignment' | 'exam' | 'full_course'>('module');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [aiCourseCategory, setAiCourseCategory] = useState('');
  const [aiModuleCount, setAiModuleCount] = useState(6);
  const [aiDifficulty, setAiDifficulty] = useState('intermediate');
  const [aiProvider, setAiProvider] = useState<'openai' | 'claude'>('claude'); // Default to Claude to avoid quota issues
  const [courseThumbnail, setCourseThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('');
  
  // Video management state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideoCourse, setSelectedVideoCourse] = useState<any>(null);
  const [videoUploads, setVideoUploads] = useState<{[key: string]: File}>({});
  const [uploadingVideos, setUploadingVideos] = useState<{[key: string]: boolean}>({});
  const [fullCourseProgress, setFullCourseProgress] = useState<{
    step: string;
    progress: number;
    currentModule: number;
    totalModules: number;
  } | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Predefined course categories
  const courseCategories = [
    'Arts & Humanities',
    'Communication', 
    'Education',
    'Engineering',
    'Finance',
    'Health and Medical',
    'Law',
    'Math',
    'Natural Sciences',
    'Social Sciences',
    'Coding'
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [coursesData, categoriesData, templatesData] = await Promise.all([
        courseService.getCourses(),
        courseService.getIndustryCategories(),
        courseService.getCourseTemplates()
      ]);
      
      setCourses(coursesData.courses);
      setIndustryCategories(categoriesData);
      setCourseTemplates(templatesData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = await getAdminToken();
      if (!token) {
        throw new Error('Admin authentication required');
      }

      const courseData = {
        ...createForm,
        instructorId: adminUser?.uid || 'platform',
        createdBy: adminUser?.uid || 'platform',
        status: 'draft' as const
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/admin/courses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(courseData)
      });

      if (!response.ok) {
        throw new Error('Failed to create course');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success('Course created successfully');
        setShowCreateModal(false);
        resetCreateForm();
        fetchInitialData(); // Refresh the course list
      } else {
        throw new Error(result.message || 'Failed to create course');
      }
    } catch (error: any) {
      console.error('Error creating course:', error);
      toast.error(error.message || 'Failed to create course');
    }
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setEditForm({
      title: course.title,
      shortDescription: course.shortDescription,
      description: course.description,
      category: course.category,
      industryCategory: course.industryCategory,
      difficulty: course.difficulty,
      duration: course.duration,
      estimatedWeeks: course.estimatedWeeks,
      price: course.price,
      instructorName: course.instructorName,
      instructorBio: course.instructorBio || '',
      thumbnailUrl: course.thumbnailUrl || '',
      skills: course.skills,
      prerequisites: course.prerequisites,
      learningObjectives: course.learningObjectives,
      template: course.template,
      tags: course.tags,
      status: course.status
    });
    setEditThumbnailPreview(course.thumbnailUrl || '');
    setShowEditModal(true);
  };

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingCourse) return;
    
    try {
      const token = await getAdminToken();
      if (!token) {
        throw new Error('Admin authentication required');
      }

      let thumbnailUrl = editForm.thumbnailUrl;
      
      // Upload new thumbnail if selected
      if (editThumbnail) {
        try {
          thumbnailUrl = await uploadThumbnailToStorage(editThumbnail);
        } catch (uploadError) {
          console.error('Thumbnail upload failed:', uploadError);
          toast.error('Failed to upload thumbnail');
          return;
        }
      }

      const courseData = {
        ...editForm,
        thumbnailUrl,
        updatedAt: new Date().toISOString(),
        lastModified: new Date().toISOString()
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/admin/courses/${editingCourse.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(courseData)
      });

      if (!response.ok) {
        throw new Error('Failed to update course');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success('Course updated successfully');
        setShowEditModal(false);
        resetEditForm();
        fetchInitialData(); // Refresh the course list
      } else {
        throw new Error(result.message || 'Failed to update course');
      }
    } catch (error: any) {
      console.error('Error updating course:', error);
      toast.error(error.message || 'Failed to update course');
    }
  };

  const handleUpdateCourseData = async (courseId: string) => {
    try {
      setRefreshing(true);
      const token = await getAdminToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      
      const response = await fetch(`/api/courses/${courseId}/update-data`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to update course data');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success('Course data updated with real-time information');
        fetchInitialData(); // Refresh the course list
      } else {
        throw new Error(result.message || 'Failed to update course data');
      }
    } catch (error: any) {
      console.error('Error updating course data:', error);
      toast.error(error.message || 'Failed to update course data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleUpdateAllCoursesData = async () => {
    try {
      setRefreshing(true);
      const token = await getAdminToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/courses/update-all-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to update all courses data');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success('All courses updated with real-time data');
        fetchInitialData(); // Refresh the course list
      } else {
        throw new Error(result.message || 'Failed to update courses data');
      }
    } catch (error: any) {
      console.error('Error updating all courses data:', error);
      toast.error(error.message || 'Failed to update all courses data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleGenerateAIContent = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a prompt for AI content generation');
      return;
    }

    // Check if user has access to AI generation features
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/subscriptions/features/aiContentGeneration`, {
        headers: {
          'Authorization': `Bearer ${await getAdminToken()}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.upgradeRequired) {
          setShowUpgradeModal(true);
          return;
        }
        throw new Error(errorData.message || 'Access check failed');
      }
    } catch (error) {
      console.error('Error checking AI generation access:', error);
      setShowUpgradeModal(true);
      return;
    }

    if (aiContentType === 'full_course') {
      if (!aiCourseCategory) {
        toast.error('Please select a course category for full course generation');
        return;
      }
      await handleFullCourseGeneration();
      return;
    }

    try {
      setAiGenerating(true);
      
      const token = await getAdminToken();
      if (!token) {
        throw new Error('Admin authentication required');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/courses/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: aiPrompt,
          contentType: aiContentType
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate AI content');
      }

      const result = await response.json();
      
      if (result.success) {
        setGeneratedContent(result.data.content);
        toast.success('AI content generated successfully');
      } else {
        throw new Error(result.message || 'Failed to generate AI content');
      }
    } catch (error: any) {
      console.error('Error generating AI content:', error);
      toast.error(error.message || 'Failed to generate AI content');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleFullCourseGeneration = async () => {
    try {
      setAiGenerating(true);
      setFullCourseProgress({
        step: 'Starting course generation...',
        progress: 0,
        currentModule: 0,
        totalModules: aiModuleCount
      });

      // Upload thumbnail if provided
      let thumbnailUrl = '';
      if (courseThumbnail) {
        setFullCourseProgress(prev => prev ? { ...prev, step: 'Uploading thumbnail...', progress: 5 } : null);
        try {
          thumbnailUrl = await uploadThumbnailToStorage(courseThumbnail);
        } catch (uploadError) {
          console.error('Thumbnail upload failed:', uploadError);
          toast.error('Failed to upload thumbnail, continuing without it');
        }
      }

      const token = await getAdminToken();
      if (!token) {
        throw new Error('Admin authentication required');
      }

      // Start progress simulation
      const progressSteps = [
        { step: 'Analyzing course requirements...', progress: 10 },
        { step: 'Generating course outline...', progress: 20 },
        { step: 'Creating course structure...', progress: 30 },
        { step: `Generating ${aiModuleCount} modules with AI...`, progress: 40 },
        { step: 'Creating quizzes and assignments...', progress: 70 },
        { step: 'Setting up assessments...', progress: 85 },
        { step: 'Finalizing course...', progress: 95 }
      ];

      let currentStepIndex = 0;
      const progressInterval = setInterval(() => {
        if (currentStepIndex < progressSteps.length) {
          setFullCourseProgress(prev => prev ? {
            ...prev,
            ...progressSteps[currentStepIndex]
          } : null);
          currentStepIndex++;
        }
      }, 8000); // Update every 8 seconds

      try {
        // Set a longer timeout for AI generation
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minute timeout

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/admin/courses/ai-generate-full`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            topic: aiPrompt,
            templateId: 'comprehensive',
            difficulty: aiDifficulty,
            estimatedWeeks: Math.ceil(aiModuleCount * 1.2),
            industryCategory: aiCourseCategory,
            instructorName: 'AI Instructor',
            instructorBio: 'Expert AI-generated course instructor with comprehensive knowledge',
            moduleCount: aiModuleCount,
            category: aiCourseCategory,
            thumbnailUrl: thumbnailUrl || '',
            aiProvider
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        clearInterval(progressInterval);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to generate full course');
        }

        const result = await response.json();
        
        if (result.success) {
          setFullCourseProgress(prev => prev ? {
            ...prev,
            step: 'Course generation completed!',
            progress: 100
          } : null);
          
          setTimeout(() => {
            toast.success('Full course generated successfully!');
            setShowAIModal(false);
            setAiPrompt('');
            setAiCourseCategory('');
            setAiModuleCount(6);
            setAiDifficulty('intermediate');
            setCourseThumbnail(null);
            setThumbnailPreview('');
            setFullCourseProgress(null);
            fetchInitialData(); // Refresh the courses list
          }, 1500);
        } else {
          clearInterval(progressInterval);
          throw new Error(result.message || 'Failed to generate full course');
        }
      } catch (error: any) {
        clearInterval(progressInterval);
        
        if (error.name === 'AbortError') {
          setFullCourseProgress(prev => prev ? {
            ...prev,
            step: 'Generation is taking longer than expected, but may still complete...',
            progress: 90
          } : null);
          
          toast('Generation is taking longer than expected. Please check back in a few minutes.', {
            icon: 'ℹ️',
            duration: 4000
          });
          
          // Don't close the modal immediately, let user see the message
          setTimeout(() => {
            setFullCourseProgress(null);
            setAiGenerating(false);
          }, 5000);
          return;
        }
        
        throw error;
      }
    } catch (error: any) {
      console.error('Error generating full course:', error);
      toast.error(error.message || 'Failed to generate full course');
      setFullCourseProgress(null);
    } finally {
      setAiGenerating(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      title: '',
      shortDescription: '',
      description: '',
      category: '',
      industryCategory: '',
      difficulty: 'beginner',
      duration: 0,
      estimatedWeeks: 0,
      price: 0,
      instructorName: '',
      instructorBio: '',
      thumbnailUrl: '',
      skills: [],
      prerequisites: [],
      learningObjectives: [],
      template: '',
      tags: []
    });
  };

  const resetEditForm = () => {
    setEditForm({
      title: '',
      shortDescription: '',
      description: '',
      category: '',
      industryCategory: '',
      difficulty: 'beginner',
      duration: 0,
      estimatedWeeks: 0,
      price: 0,
      instructorName: '',
      instructorBio: '',
      thumbnailUrl: '',
      skills: [],
      prerequisites: [],
      learningObjectives: [],
      template: '',
      tags: [],
      status: 'draft'
    });
    setEditingCourse(null);
    setEditThumbnail(null);
    setEditThumbnailPreview('');
  };

  const handleArrayInput = (field: 'skills' | 'prerequisites' | 'learningObjectives' | 'tags', value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    setCreateForm(prev => ({ ...prev, [field]: items }));
  };

  const handleEditArrayInput = (field: 'skills' | 'prerequisites' | 'learningObjectives' | 'tags', value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    setEditForm(prev => ({ ...prev, [field]: items }));
  };

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be smaller than 5MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      setCourseThumbnail(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be smaller than 5MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      setEditThumbnail(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditThumbnailPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadThumbnailToStorage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', 'course-thumbnail');
    
    try {
      const token = await getAdminToken();
      if (!token) {
        throw new Error('Admin authentication required');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload image');
      }
      
      const result = await response.json();
      return result.data.url;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleVideoUpload = (contentKey: string, file: File) => {
    // Validate video file
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    
    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video must be smaller than 100MB');
      return;
    }
    
    setVideoUploads(prev => ({ ...prev, [contentKey]: file }));
  };

  const uploadVideoToContent = async (courseId: string, moduleId: string, contentId: string, file: File) => {
    const contentKey = `${courseId}-${moduleId}-${contentId}`;
    
    try {
      setUploadingVideos(prev => ({ ...prev, [contentKey]: true }));
      
      const token = await getAdminToken();
      if (!token) {
        throw new Error('Admin authentication required');
      }
      
      const formData = new FormData();
      formData.append('video', file);
      formData.append('courseId', courseId);
      formData.append('moduleId', moduleId);
      formData.append('contentId', contentId);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/upload/video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload video');
      }
      
      const result = await response.json();
      toast.success('Video uploaded successfully!');
      
      // Remove from pending uploads
      setVideoUploads(prev => {
        const updated = { ...prev };
        delete updated[contentKey];
        return updated;
      });
      
      // Refresh course data
      await fetchInitialData();
      
      return result.data.url;
    } catch (error: any) {
      console.error('Video upload error:', error);
      toast.error('Failed to upload video: ' + (error.message || 'Unknown error'));
      throw error;
    } finally {
      setUploadingVideos(prev => ({ ...prev, [contentKey]: false }));
    }
  };

  const openVideoModal = (course: any) => {
    setSelectedVideoCourse(course);
    setShowVideoModal(true);
  };

  // Check if predefined courses exist
  useEffect(() => {
    if (courses.length > 0) {
      const existenceMap: {[key: string]: boolean} = {};
      predefinedCourses.forEach(course => {
        existenceMap[course.key] = courses.some(c => c.id === course.data.id);
      });
      setCourseExists(existenceMap);
    }
  }, [courses]);

  // Generic function to create any predefined course
  const createPredefinedCourse = async (courseKey: string) => {
    const courseConfig = predefinedCourses.find(c => c.key === courseKey);
    if (!courseConfig) return;

    try {
      setCreatingCourses(prev => ({...prev, [courseKey]: true}));
      
      // Ensure we have a Firebase auth token first
      if (!auth.currentUser) {
        toast.error('Please log in first');
        return;
      }

      // Get fresh Firebase ID token and store it in localStorage only
      // Let apiService interceptor handle adding it to requests
      const firebaseToken = await auth.currentUser.getIdToken(true);
      localStorage.setItem('authToken', firebaseToken);

      const courseDataToSend = {
        ...courseConfig.data,
        modules: courseConfig.data.modules
      };

      console.log(`Creating ${courseConfig.name}...`, courseDataToSend);
      console.log('Token stored in localStorage for interceptor');
      
      // Show progress toast for large courses
      const progressToast = toast.loading(`Creating ${courseConfig.name}... This may take up to 3 minutes for comprehensive courses.`);
      
      // Let apiService interceptor automatically add the token
      const response = await api.post('/courses', courseDataToSend);
      
      // Dismiss the progress toast
      toast.dismiss(progressToast);
      
      if (response.data.success) {
        toast.success(`${courseConfig.name} created successfully!`);
        await fetchInitialData();
      } else {
        throw new Error(response.data.message || `Failed to create ${courseConfig.name}`);
      }
    } catch (error: any) {
      console.error(`Error creating ${courseConfig.name}:`, error);
      if (error.response?.status === 409) {
        toast.success(`${courseConfig.name} already exists`);
        setCourseExists(prev => ({...prev, [courseKey]: true}));
      } else if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        toast.error(`${courseConfig.name} upload timed out. The course may still be processing in the background. Please check again in a few minutes.`);
      } else {
        toast.error(`Failed to create ${courseConfig.name}: ` + (error.response?.data?.message || error.message));
      }
    } finally {
      setCreatingCourses(prev => ({...prev, [courseKey]: false}));
    }
  };

  // Update course statuses from draft to published
  const updateCourseStatuses = async () => {
    try {
      setUpdatingStatuses(true);
      
      // Ensure we have a Firebase auth token first
      if (!auth.currentUser) {
        toast.error('Please log in first');
        return;
      }

      // Get fresh Firebase ID token
      const firebaseToken = await auth.currentUser.getIdToken(true);
      localStorage.setItem('authToken', firebaseToken);

      console.log('Updating course statuses to published...');
      
      const response = await api.post('/courses/admin/update-comprehensive-status');
      
      if (response.data.success) {
        toast.success(response.data.message || 'Course statuses updated successfully');
        await fetchInitialData(); // Refresh the course list
      } else {
        throw new Error(response.data.message || 'Failed to update course statuses');
      }
    } catch (error: any) {
      console.error('Error updating course statuses:', error);
      toast.error('Failed to update course statuses: ' + (error.response?.data?.message || error.message));
    } finally {
      setUpdatingStatuses(false);
    }
  };

  // Helper function to get color classes for each course
  const getColorClasses = (color: string) => {
    const colors: {[key: string]: {bg: string, border: string, text: string, button: string, hover: string}} = {
      purple: { bg: 'from-purple-50 to-indigo-50', border: 'border-purple-200', text: 'text-purple-900', button: 'bg-purple-600 hover:bg-purple-700', hover: 'bg-purple-400' },
      green: { bg: 'from-green-50 to-emerald-50', border: 'border-green-200', text: 'text-green-900', button: 'bg-green-600 hover:bg-green-700', hover: 'bg-green-400' },
      blue: { bg: 'from-blue-50 to-sky-50', border: 'border-blue-200', text: 'text-blue-900', button: 'bg-blue-600 hover:bg-blue-700', hover: 'bg-blue-400' },
      indigo: { bg: 'from-indigo-50 to-purple-50', border: 'border-indigo-200', text: 'text-indigo-900', button: 'bg-indigo-600 hover:bg-indigo-700', hover: 'bg-indigo-400' },
      emerald: { bg: 'from-emerald-50 to-green-50', border: 'border-emerald-200', text: 'text-emerald-900', button: 'bg-emerald-600 hover:bg-emerald-700', hover: 'bg-emerald-400' },
      cyan: { bg: 'from-cyan-50 to-blue-50', border: 'border-cyan-200', text: 'text-cyan-900', button: 'bg-cyan-600 hover:bg-cyan-700', hover: 'bg-cyan-400' },
      amber: { bg: 'from-amber-50 to-yellow-50', border: 'border-amber-200', text: 'text-amber-900', button: 'bg-amber-600 hover:bg-amber-700', hover: 'bg-amber-400' },
      rose: { bg: 'from-rose-50 to-pink-50', border: 'border-rose-200', text: 'text-rose-900', button: 'bg-rose-600 hover:bg-rose-700', hover: 'bg-rose-400' },
      violet: { bg: 'from-violet-50 to-purple-50', border: 'border-violet-200', text: 'text-violet-900', button: 'bg-violet-600 hover:bg-violet-700', hover: 'bg-violet-400' },
      orange: { bg: 'from-orange-50 to-red-50', border: 'border-orange-200', text: 'text-orange-900', button: 'bg-orange-600 hover:bg-orange-700', hover: 'bg-orange-400' },
      teal: { bg: 'from-teal-50 to-cyan-50', border: 'border-teal-200', text: 'text-teal-900', button: 'bg-teal-600 hover:bg-teal-700', hover: 'bg-teal-400' },
      slate: { bg: 'from-slate-50 to-gray-50', border: 'border-slate-200', text: 'text-slate-900', button: 'bg-slate-600 hover:bg-slate-700', hover: 'bg-slate-400' },
      lime: { bg: 'from-lime-50 to-green-50', border: 'border-lime-200', text: 'text-lime-900', button: 'bg-lime-600 hover:bg-lime-700', hover: 'bg-lime-400' },
      pink: { bg: 'from-pink-50 to-rose-50', border: 'border-pink-200', text: 'text-pink-900', button: 'bg-pink-600 hover:bg-pink-700', hover: 'bg-pink-400' },
      gray: { bg: 'from-gray-50 to-slate-50', border: 'border-gray-200', text: 'text-gray-900', button: 'bg-gray-600 hover:bg-gray-700', hover: 'bg-gray-400' },
      sky: { bg: 'from-sky-50 to-blue-50', border: 'border-sky-200', text: 'text-sky-900', button: 'bg-sky-600 hover:bg-sky-700', hover: 'bg-sky-400' },
      fuchsia: { bg: 'from-fuchsia-50 to-pink-50', border: 'border-fuchsia-200', text: 'text-fuchsia-900', button: 'bg-fuchsia-600 hover:bg-fuchsia-700', hover: 'bg-fuchsia-400' },
      red: { bg: 'from-red-50 to-rose-50', border: 'border-red-200', text: 'text-red-900', button: 'bg-red-600 hover:bg-red-700', hover: 'bg-red-400' },
      yellow: { bg: 'from-yellow-50 to-amber-50', border: 'border-yellow-200', text: 'text-yellow-900', button: 'bg-yellow-600 hover:bg-yellow-700', hover: 'bg-yellow-400' },
      zinc: { bg: 'from-zinc-50 to-gray-50', border: 'border-zinc-200', text: 'text-zinc-900', button: 'bg-zinc-600 hover:bg-zinc-700', hover: 'bg-zinc-400' },
      stone: { bg: 'from-stone-50 to-zinc-50', border: 'border-stone-200', text: 'text-stone-900', button: 'bg-stone-600 hover:bg-stone-700', hover: 'bg-stone-400' },
      neutral: { bg: 'from-neutral-50 to-gray-50', border: 'border-neutral-200', text: 'text-neutral-900', button: 'bg-neutral-600 hover:bg-neutral-700', hover: 'bg-neutral-400' }
    };
    return colors[color] || colors.gray;
  };

  const filteredCourses = courses.filter(course => {
    const matchesCategory = selectedCategory === 'all' || course.industryCategory === selectedCategory;
    const matchesSearch = !searchQuery || 
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (course.skills || []).some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  const formatSalary = (salary: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(salary);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Course Management</h1>
            <p className="text-gray-600 mt-2">
              Create and manage courses with AI assistance and real-time market data
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAIModal(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <SparklesIcon className="h-5 w-5" />
              AI Assistant
            </button>
            <button
              onClick={handleUpdateAllCoursesData}
              disabled={refreshing}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
              Update All Data
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Create Course
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <AcademicCapIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Courses</p>
                <p className="text-2xl font-bold text-gray-900">{courses.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Courses</p>
                <p className="text-2xl font-bold text-gray-900">
                  {courses.filter(c => c.status === 'published').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserGroupIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Enrollments</p>
                <p className="text-2xl font-bold text-gray-900">
                  {courses.reduce((sum, course) => sum + (course.enrollmentCount || 0), 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <StarIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Rating</p>
                <p className="text-2xl font-bold text-gray-900">
                  {courses.length > 0 
                    ? (courses.reduce((sum, course) => sum + course.averageRating, 0) / courses.length).toFixed(1)
                    : '0.0'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Predefined Courses */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Predefined Courses</h2>
              <p className="text-gray-600 mt-1">Deploy comprehensive courses with pre-built modules, exercises, and assessments.</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={updateCourseStatuses}
                disabled={updatingStatuses}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
              >
                {updatingStatuses ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    <span>Publish All Courses</span>
                  </>
                )}
              </button>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideDeployed}
                  onChange={(e) => setHideDeployed(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Hide deployed courses</span>
              </label>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {predefinedCourses
              .filter(course => !hideDeployed || !courseExists[course.key])
              .map((course) => {
              const colors = getColorClasses(course.color);
              const isCreating = creatingCourses[course.key] || false;
              const exists = courseExists[course.key] || false;
              
              return (
                <div key={course.key} className={`border ${colors.border} rounded-lg p-4 bg-gradient-to-r ${colors.bg}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className={`text-lg font-semibold ${colors.text}`}>{course.icon} {course.name}</h3>
                      <p className={`${colors.text} opacity-80 text-sm mt-1`}>{course.description}</p>
                    </div>
                    {exists && (
                      <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">
                        ✓ Deployed
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 gap-1 text-xs mb-3" style={{color: `var(--tw-${course.color}-700)`}}>
                    {course.features.slice(0, 4).map((feature, index) => (
                      <div key={index}>• {feature}</div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => createPredefinedCourse(course.key)}
                    disabled={isCreating || exists}
                    className={`w-full py-2 px-3 rounded-lg font-medium transition-colors text-sm ${
                      exists
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : isCreating
                        ? `${colors.hover} text-white cursor-not-allowed`
                        : `${colors.button} text-white`
                    }`}
                  >
                    {isCreating ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                        Creating...
                      </span>
                    ) : exists ? (
                      'Already Deployed'
                    ) : (
                      'Deploy Course'
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {industryCategories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Courses Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Course
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Market Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCourses.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {course.thumbnailUrl ? (
                            <img 
                              className="h-10 w-10 rounded-lg object-cover" 
                              src={course.thumbnailUrl} 
                              alt={course.title}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                              <AcademicCapIcon className="h-6 w-6 text-indigo-600" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {course.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            by {course.instructorName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{course.category}</div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(course.difficulty)}`}>
                        {course.difficulty}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-green-600">
                        <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                        {formatSalary(course.marketData?.averageSalary || 0)}
                      </div>
                      <div className="flex items-center text-sm text-purple-600">
                        <TrophyIcon className="h-4 w-4 mr-1" />
                        {course.marketData?.jobDemand || 0} jobs
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {(course.enrollmentCount || 0).toLocaleString()} enrolled
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                        {(course.averageRating || 0).toFixed(1)} ({course.reviewCount || 0} reviews)
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        course.status === 'published' ? 'bg-green-100 text-green-800' :
                        course.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {course.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUpdateCourseData(course.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Update with real-time data"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setSelectedCourse(course)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditCourse(course)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit Course"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openVideoModal(course)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Manage Videos"
                        >
                          <VideoCameraIcon className="h-4 w-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Course Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Create New Course</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateCourse} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={createForm.title}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={createForm.category}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry Category
                  </label>
                  <select
                    value={createForm.industryCategory}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, industryCategory: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select Industry</option>
                    {industryCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty Level
                  </label>
                  <select
                    value={createForm.difficulty}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, difficulty: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (hours)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={createForm.duration}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Weeks
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={createForm.estimatedWeeks}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, estimatedWeeks: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Template
                  </label>
                  <select
                    value={createForm.template}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, template: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select Template</option>
                    {courseTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} - {template.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instructor Name
                  </label>
                  <input
                    type="text"
                    value={createForm.instructorName}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, instructorName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Short Description
                </label>
                <input
                  type="text"
                  value={createForm.shortDescription}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, shortDescription: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Description
                </label>
                <textarea
                  rows={4}
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skills (comma-separated) *
                </label>
                <input
                  type="text"
                  required
                  placeholder="React, JavaScript, Node.js"
                  onChange={(e) => handleArrayInput('skills', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prerequisites (comma-separated)
                </label>
                <input
                  type="text"
                  placeholder="Basic JavaScript, HTML/CSS"
                  onChange={(e) => handleArrayInput('prerequisites', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Learning Objectives (comma-separated)
                </label>
                <input
                  type="text"
                  placeholder="Build web applications, Master React framework"
                  onChange={(e) => handleArrayInput('learningObjectives', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Create Course
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Course Modal */}
      {showEditModal && editingCourse && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Edit Course</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetEditForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateCourse} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={editForm.title}
                    onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={editForm.category}
                    onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry Category
                  </label>
                  <select
                    value={editForm.industryCategory}
                    onChange={(e) => setEditForm(prev => ({ ...prev, industryCategory: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select Industry</option>
                    {industryCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty Level
                  </label>
                  <select
                    value={editForm.difficulty}
                    onChange={(e) => setEditForm(prev => ({ ...prev, difficulty: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (hours)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.duration}
                    onChange={(e) => setEditForm(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Weeks
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.estimatedWeeks}
                    onChange={(e) => setEditForm(prev => ({ ...prev, estimatedWeeks: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.price}
                    onChange={(e) => setEditForm(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Template
                  </label>
                  <select
                    value={editForm.template}
                    onChange={(e) => setEditForm(prev => ({ ...prev, template: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select Template</option>
                    {courseTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} - {template.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instructor Name
                  </label>
                  <input
                    type="text"
                    value={editForm.instructorName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, instructorName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Short Description
                </label>
                <input
                  type="text"
                  value={editForm.shortDescription}
                  onChange={(e) => setEditForm(prev => ({ ...prev, shortDescription: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Description
                </label>
                <textarea
                  rows={4}
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructor Bio
                </label>
                <textarea
                  rows={3}
                  value={editForm.instructorBio}
                  onChange={(e) => setEditForm(prev => ({ ...prev, instructorBio: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Brief biography of the course instructor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Thumbnail
                </label>
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditThumbnailUpload}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP up to 5MB</p>
                  </div>
                  {editThumbnailPreview && (
                    <div className="flex-shrink-0">
                      <img
                        src={editThumbnailPreview}
                        alt="Thumbnail preview"
                        className="h-16 w-16 rounded-lg object-cover border border-gray-300"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skills (comma-separated) *
                </label>
                <input
                  type="text"
                  required
                  placeholder="React, JavaScript, Node.js"
                  value={editForm.skills.join(', ')}
                  onChange={(e) => handleEditArrayInput('skills', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prerequisites (comma-separated)
                </label>
                <input
                  type="text"
                  placeholder="Basic JavaScript, HTML/CSS"
                  value={editForm.prerequisites.join(', ')}
                  onChange={(e) => handleEditArrayInput('prerequisites', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Learning Objectives (comma-separated)
                </label>
                <input
                  type="text"
                  placeholder="Build web applications, Master React framework"
                  value={editForm.learningObjectives.join(', ')}
                  onChange={(e) => handleEditArrayInput('learningObjectives', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  placeholder="React, Frontend, JavaScript"
                  value={editForm.tags.join(', ')}
                  onChange={(e) => handleEditArrayInput('tags', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetEditForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Update Course
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Assistant Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-purple-600" />
                AI Content Generator
              </h3>
              <button
                onClick={() => setShowAIModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content Type
                </label>
                <select
                  value={aiContentType}
                  onChange={(e) => setAiContentType(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="module">Course Module</option>
                  <option value="quiz">Quiz Questions</option>
                  <option value="assignment">Assignment</option>
                  <option value="exam">Exam Questions</option>
                  <option value="full_course">🚀 Full Course Generation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Prompt
                </label>
                <textarea
                  rows={4}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={
                    aiContentType === 'full_course' 
                      ? "Enter the course topic (e.g., 'Finance', 'Digital Marketing', 'Data Science'). AI will generate a complete course with modules, quizzes, assignments, midterm, and final exam."
                      : "Describe what you want to generate. For example: 'Create a module about React hooks with practical examples and exercises for intermediate developers.'"
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {aiContentType === 'full_course' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Course Category *
                      </label>
                      <select
                        value={aiCourseCategory}
                        onChange={(e) => setAiCourseCategory(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      >
                        <option value="">Select Category</option>
                        {courseCategories.map(category => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Modules
                      </label>
                      <input
                        type="number"
                        min="3"
                        max="12"
                        value={aiModuleCount}
                        onChange={(e) => setAiModuleCount(parseInt(e.target.value) || 6)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="6"
                      />
                      <p className="text-xs text-gray-500 mt-1">Recommended: 4-8 modules</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        AI Provider
                      </label>
                      <select
                        value={aiProvider}
                        onChange={(e) => setAiProvider(e.target.value as 'openai' | 'claude')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="claude">Claude (Anthropic)</option>
                        <option value="openai">OpenAI (GPT)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Choose your preferred AI provider</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Difficulty Level
                      </label>
                      <select
                        value={aiDifficulty}
                        onChange={(e) => setAiDifficulty(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Course Thumbnail (Optional)
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailUpload}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                        />
                        <p className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP up to 5MB</p>
                      </div>
                      {thumbnailPreview && (
                        <div className="flex-shrink-0">
                          <img
                            src={thumbnailPreview}
                            alt="Thumbnail preview"
                            className="h-16 w-16 rounded-lg object-cover border border-gray-300"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-start gap-3">
                      <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-blue-900 mb-2">AI Course Generation</h4>
                        <p className="text-sm text-blue-700 mb-3">
                          This will create a complete course with category-appropriate interactive components:
                        </p>
                        <ul className="text-sm text-blue-700 space-y-1 mb-3">
                          <li>• Comprehensive syllabus and course outline</li>
                          <li>• {aiModuleCount} modules with detailed content</li>
                          <li>• Interactive exercises for {aiCourseCategory || 'selected category'}</li>
                          <li>• Quizzes for each module</li>
                          <li>• Assignments with rubrics</li>
                          <li>• Midterm and final exams</li>
                          <li>• Subject-specific interactive components</li>
                          <li>• Automatic template selection (comprehensive)</li>
                        </ul>
                        <div className="bg-white rounded p-2 text-xs">
                          <strong>Interactive Components by Category:</strong>
                          <div className="mt-1 space-y-1">
                            <div>• <strong>Coding:</strong> InteractiveExercise.tsx</div>
                            <div>• <strong>Finance:</strong> FinanceInteractive.tsx</div>
                            <div>• <strong>Math:</strong> MathInteractive.tsx</div>
                            <div>• <strong>Engineering:</strong> EngineeringTechInteractive.tsx</div>
                            <div>• <strong>Health/Medical:</strong> HealthMedicalInteractive.tsx</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {fullCourseProgress && (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="animate-spin h-5 w-5 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
                    <span className="text-sm font-medium text-gray-900">{fullCourseProgress.step}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${fullCourseProgress.progress}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-600">
                    {fullCourseProgress.totalModules > 0 && (
                      <span>Module {fullCourseProgress.currentModule} of {fullCourseProgress.totalModules}</span>
                    )}
                    <span className="ml-2">{fullCourseProgress.progress}% complete</span>
                  </div>
                </div>
              )}

              <button
                onClick={handleGenerateAIContent}
                disabled={aiGenerating}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {aiGenerating ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4" />
                    Generate Content
                  </>
                )}
              </button>

              {generatedContent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Generated Content
                  </label>
                  <textarea
                    rows={12}
                    value={generatedContent}
                    onChange={(e) => setGeneratedContent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <div className="flex justify-end mt-3">
                    <button
                      onClick={() => navigator.clipboard.writeText(generatedContent)}
                      className="text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      Copy to Clipboard
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video Management Modal */}
      {showVideoModal && selectedVideoCourse && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Manage Videos - {selectedVideoCourse.title}
              </h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-start gap-3">
                  <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Video Upload Instructions</h4>
                    <p className="text-sm text-blue-700">
                      Upload videos to replace placeholder content in your course modules. Videos are stored securely in Firebase Storage and will be automatically linked to the course content.
                    </p>
                  </div>
                </div>
              </div>

              {/* Module Videos */}
              {(selectedVideoCourse as any).modules?.map((module: any, moduleIndex: number) => (
                <div key={module.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Module {moduleIndex + 1}: {module.title}
                  </h4>
                  
                  {/* Video Content Items */}
                  <div className="space-y-3">
                    {module.content?.filter((content: any) => content.type === 'video').map((videoContent: any) => {
                      const contentKey = `${selectedVideoCourse.id}-${module.id}-${videoContent.id}`;
                      const hasVideo = videoContent.metadata?.videoUrl;
                      const pendingUpload = videoUploads[contentKey];
                      const isUploading = uploadingVideos[contentKey];
                      
                      return (
                        <div key={videoContent.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900 mb-1">
                                {videoContent.title}
                              </h5>
                              <p className="text-sm text-gray-600 mb-2">
                                {videoContent.content}
                              </p>
                              <div className="flex items-center gap-4 text-sm">
                                <span className="text-gray-500">
                                  Duration: {videoContent.duration || 0} minutes
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  hasVideo 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {hasVideo ? 'Has Video' : 'Placeholder'}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex flex-col items-end gap-2">
                              {hasVideo && (
                                <a
                                  href={videoContent.metadata.videoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                  View Current Video
                                </a>
                              )}
                              
                              <div className="flex items-center gap-2">
                                <input
                                  type="file"
                                  accept="video/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleVideoUpload(contentKey, file);
                                    }
                                  }}
                                  className="hidden"
                                  id={`video-${contentKey}`}
                                />
                                <label
                                  htmlFor={`video-${contentKey}`}
                                  className="cursor-pointer inline-flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-700 text-sm rounded-md hover:bg-indigo-100"
                                >
                                  <CloudArrowUpIcon className="h-4 w-4" />
                                  Select Video
                                </label>
                                
                                {pendingUpload && (
                                  <button
                                    onClick={() => uploadVideoToContent(
                                      selectedVideoCourse.id,
                                      module.id,
                                      videoContent.id,
                                      pendingUpload
                                    )}
                                    disabled={isUploading}
                                    className={`px-3 py-1 text-sm rounded-md ${
                                      isUploading
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                    }`}
                                  >
                                    {isUploading ? (
                                      <div className="flex items-center gap-1">
                                        <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full"></div>
                                        Uploading...
                                      </div>
                                    ) : (
                                      'Upload'
                                    )}
                                  </button>
                                )}
                              </div>
                              
                              {pendingUpload && (
                                <div className="text-xs text-gray-500 text-right">
                                  Selected: {pendingUpload.name}
                                  <br />
                                  Size: {(pendingUpload.size / (1024 * 1024)).toFixed(1)} MB
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {module.content?.filter((content: any) => content.type === 'video').length === 0 && (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No video content in this module
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {(!(selectedVideoCourse as any).modules || (selectedVideoCourse as any).modules.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  No modules found in this course
                </div>
              )}
            </div>
            
            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowVideoModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <SparklesIcon className="h-6 w-6 text-purple-600" />
                AI Generation Premium Feature
              </h3>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-purple-100 to-indigo-100 mb-4">
                <SparklesIcon className="h-8 w-8 text-purple-600" />
              </div>
              
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Unlock AI-Powered Course Generation
              </h4>
              
              <p className="text-gray-600 mb-6">
                Get access to cutting-edge AI that creates comprehensive courses with the most relevant, up-to-date content based on industry trends and top-performing educational materials.
              </p>

              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 mb-6">
                <h5 className="font-semibold text-gray-900 mb-2">✨ Premium AI Features:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Advanced content generation with latest industry data</li>
                  <li>• Curated from top-performing courses and materials</li>
                  <li>• Real-time market insights integration</li>
                  <li>• Quality-assured by industry experts</li>
                  <li>• Unlimited AI-generated course modules</li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    window.open('/subscription', '_blank');
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all font-medium flex items-center justify-center gap-2"
                >
                  <SparklesIcon className="h-5 w-5" />
                  Upgrade to Premium
                </button>
                
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="w-full text-gray-600 hover:text-gray-800 py-2 text-sm"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseManagement;